import React, { useState, useEffect } from 'react';
import {
  ChartBarIcon,
  EnvelopeIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { supabase } from '../../services/supabaseClient';

interface Analytics {
  timestamp: string;
  lapsedOverview: {
    total: number;
    active: number;
    reactivated: number;
    connectionBroken: number;
    byReason: Record<string, number>;
    daysLapsedBuckets: Record<string, number>;
  };
  emailPerformance: {
    totalEmailsSent: number;
    uniqueAccountsNotified: number;
    avgEmailsPerAccount: number;
    weeklyBreakdown: Record<string, any>;
  };
  conversionMetrics: {
    totalLapsed: number;
    reactivated: number;
    conversionRate: number;
    avgEmailsBeforeConversion: number;
    totalPotentialRevenue: number;
    funnel: {
      lapsed: number;
      notified: number;
      multipleNotifications: number;
      reactivated: number;
    };
  };
  driftStats: {
    totalAccounts: number;
    accountsWithDrift: number;
    driftDetectionRate: number;
    totalNewFlows: number;
    totalUpdatedFlows: number;
    totalEstimatedHours: number;
    totalEstimatedCost: number;
  };
  connectionIssues: {
    total: number;
    affectedAccounts: number;
    recentIssues: Array<{
      orgName: string;
      userEmail: string;
      detectedAt: string;
    }>;
  };
  topOpportunities: Array<{
    accountName: string;
    userEmail: string;
    newFlows: number;
    updatedFlows: number;
    estimatedValue: number;
    daysLapsed: number;
    emailsSent: number;
  }>;
}

export default function ReactivationDashboard() {
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'opportunities' | 'emails' | 'issues'>('overview');

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);

      // Get the current session token
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('No active session');
      }

      const response = await fetch('/api/admin/reactivation-analytics', {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch analytics');
      }

      const data = await response.json();
      setAnalytics(data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading reactivation analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error loading analytics: {error}</p>
          <button
            onClick={fetchAnalytics}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return <div className="p-8">No data available</div>;
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reactivation Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Last updated: {new Date(analytics.timestamp).toLocaleString()}
          </p>
        </div>
        <button
          onClick={fetchAnalytics}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <ArrowPathIcon className="h-5 w-5" />
          Refresh
        </button>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricCard
          title="Active Lapsed Accounts"
          value={analytics.lapsedOverview.active}
          icon={<UserGroupIcon className="h-6 w-6" />}
          color="blue"
        />
        <MetricCard
          title="Conversion Rate"
          value={`${analytics.conversionMetrics.conversionRate}%`}
          icon={<ChartBarIcon className="h-6 w-6" />}
          color="green"
        />
        <MetricCard
          title="Total Emails Sent"
          value={analytics.emailPerformance.totalEmailsSent}
          icon={<EnvelopeIcon className="h-6 w-6" />}
          color="purple"
        />
        <MetricCard
          title="Potential Revenue"
          value={`$${analytics.conversionMetrics.totalPotentialRevenue.toLocaleString()}`}
          icon={<CurrencyDollarIcon className="h-6 w-6" />}
          color="yellow"
        />
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <TabButton
            active={activeTab === 'overview'}
            onClick={() => setActiveTab('overview')}
            label="Overview"
          />
          <TabButton
            active={activeTab === 'opportunities'}
            onClick={() => setActiveTab('opportunities')}
            label="Top Opportunities"
            badge={analytics.topOpportunities.length}
          />
          <TabButton
            active={activeTab === 'emails'}
            onClick={() => setActiveTab('emails')}
            label="Email Performance"
          />
          <TabButton
            active={activeTab === 'issues'}
            onClick={() => setActiveTab('issues')}
            label="Connection Issues"
            badge={analytics.connectionIssues.total}
            badgeColor="red"
          />
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <OverviewTab analytics={analytics} />
      )}

      {activeTab === 'opportunities' && (
        <OpportunitiesTab opportunities={analytics.topOpportunities} />
      )}

      {activeTab === 'emails' && (
        <EmailPerformanceTab emailPerformance={analytics.emailPerformance} />
      )}

      {activeTab === 'issues' && (
        <ConnectionIssuesTab connectionIssues={analytics.connectionIssues} />
      )}
    </div>
  );
}

// Metric Card Component
function MetricCard({ title, value, icon, color }: any) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    red: 'bg-red-50 text-red-600',
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
      </div>
      <div className="mt-4">
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
      </div>
    </div>
  );
}

// Tab Button Component
function TabButton({ active, onClick, label, badge, badgeColor = 'blue' }: any) {
  return (
    <button
      onClick={onClick}
      className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
        active
          ? 'border-blue-500 text-blue-600'
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
      }`}
    >
      {label}
      {badge !== undefined && (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
          badgeColor === 'red' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
        }`}>
          {badge}
        </span>
      )}
    </button>
  );
}

// Overview Tab
function OverviewTab({ analytics }: { analytics: Analytics }) {
  return (
    <div className="space-y-6">
      {/* Conversion Funnel */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Conversion Funnel</h3>
        <div className="space-y-3">
          <FunnelStep
            label="Lapsed Accounts"
            value={analytics.conversionMetrics.funnel.lapsed}
            total={analytics.conversionMetrics.funnel.lapsed}
          />
          <FunnelStep
            label="Notified (Emails Sent)"
            value={analytics.conversionMetrics.funnel.notified}
            total={analytics.conversionMetrics.funnel.lapsed}
          />
          <FunnelStep
            label="Multiple Notifications"
            value={analytics.conversionMetrics.funnel.multipleNotifications}
            total={analytics.conversionMetrics.funnel.lapsed}
          />
          <FunnelStep
            label="Reactivated"
            value={analytics.conversionMetrics.funnel.reactivated}
            total={analytics.conversionMetrics.funnel.lapsed}
            isLast
          />
        </div>
      </div>

      {/* Drift Detection Stats */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Drift Detection</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatBox label="New Flows" value={analytics.driftStats.totalNewFlows} />
          <StatBox label="Updated Flows" value={analytics.driftStats.totalUpdatedFlows} />
          <StatBox label="Total Hours" value={`${analytics.driftStats.totalEstimatedHours}h`} />
          <StatBox label="Total Value" value={`$${analytics.driftStats.totalEstimatedCost.toLocaleString()}`} />
        </div>
      </div>

      {/* Lapsed by Reason */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Lapsed by Reason</h3>
        <div className="space-y-2">
          {Object.entries(analytics.lapsedOverview.byReason).map(([reason, count]) => (
            <div key={reason} className="flex justify-between items-center">
              <span className="text-sm text-gray-600 capitalize">{reason.replace(/_/g, ' ')}</span>
              <span className="text-sm font-semibold text-gray-900">{count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Opportunities Tab
function OpportunitiesTab({ opportunities }: { opportunities: any[] }) {
  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">New/Updated</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Est. Value</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days Lapsed</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Emails Sent</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {opportunities.map((opp, idx) => (
            <tr key={idx} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {opp.accountName}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                {opp.userEmail}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                {opp.newFlows} / {opp.updatedFlows}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                ${opp.estimatedValue.toLocaleString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                {opp.daysLapsed} days
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                {opp.emailsSent}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Email Performance Tab
function EmailPerformanceTab({ emailPerformance }: { emailPerformance: any }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">Total Emails</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{emailPerformance.totalEmailsSent}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">Unique Accounts</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{emailPerformance.uniqueAccountsNotified}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">Avg per Account</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{emailPerformance.avgEmailsPerAccount}</p>
        </div>
      </div>

      {/* Weekly breakdown would go here */}
    </div>
  );
}

// Connection Issues Tab
function ConnectionIssuesTab({ connectionIssues }: { connectionIssues: any }) {
  return (
    <div className="space-y-6">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center">
          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2" />
          <p className="text-yellow-800">
            {connectionIssues.total} org(s) with broken connections affecting {connectionIssues.affectedAccounts} account(s)
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Org Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Detected</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {connectionIssues.recentIssues.map((issue: any, idx: number) => (
              <tr key={idx} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{issue.orgName}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{issue.userEmail}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {new Date(issue.detectedAt).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Helper Components
function FunnelStep({ label, value, total, isLast = false }: any) {
  const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;

  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <span className="text-sm text-gray-600">{value} ({percentage}%)</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`h-2 rounded-full ${isLast ? 'bg-green-500' : 'bg-blue-500'}`}
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
    </div>
  );
}

function StatBox({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="text-center p-4 bg-gray-50 rounded-lg">
      <p className="text-sm text-gray-600">{label}</p>
      <p className="text-xl font-bold text-gray-900 mt-1">{value}</p>
    </div>
  );
}
